import { useCallback, useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { robinhoodApi, type RobinhoodStatus } from '../../lib/api'
import { cn } from '../../lib/utils'

const when = (t: number) => new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2 text-[12.5px]">
      <span className="shrink-0 text-fg-muted">{label}</span>
      <span className="min-w-0 text-right text-fg-subtle">{children}</span>
    </div>
  )
}

/** Connect the admin's Robinhood Agentic account (sign-in happens on robinhood.com; the server keeps the tokens). */
export function RobinhoodSettings() {
  const [st, setSt] = useState<RobinhoodStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmOff, setConfirmOff] = useState(false)
  const [tools, setTools] = useState<{ name: string; description: string }[] | null>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)
  // The sign-in URL while a login is open: the user approves there, then pastes the address it ends on.
  const [signIn, setSignIn] = useState<string | null>(null)
  const [pasted, setPasted] = useState('')

  const load = useCallback(async () => {
    try {
      setSt(await robinhoodApi.status())
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Could not load the Robinhood status') })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const connect = async () => {
    setBusy(true)
    setMsg(null)
    try {
      const { url } = await robinhoodApi.start()
      setSignIn(url)
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Robinhood did not start the sign-in') })
    } finally {
      setBusy(false)
    }
  }
  const finish = async () => {
    setBusy(true)
    setMsg(null)
    try {
      await robinhoodApi.finish(pasted)
      setSignIn(null)
      setPasted('')
      setMsg({ tone: 'ok', text: 'Robinhood is connected.' })
      await load()
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Robinhood did not complete the sign-in') })
    } finally {
      setBusy(false)
    }
  }
  const check = async () => {
    setBusy(true)
    setMsg(null)
    try {
      setTools((await robinhoodApi.tools()).tools)
      await load()
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Robinhood did not answer') })
      await load()
    } finally {
      setBusy(false)
    }
  }
  const disconnect = async () => {
    setBusy(true)
    try {
      await robinhoodApi.disconnect()
      setConfirmOff(false)
      setTools(null)
      setMsg({ tone: 'ok', text: 'Disconnected — the tokens were deleted from this server.' })
      await load()
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Could not disconnect') })
    } finally {
      setBusy(false)
    }
  }

  const needsLogin = st?.status === 'needs_login'

  return (
    <section id="settings-robinhood" className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-fg">Broker · Robinhood</h2>
          <p className="mt-0.5 text-[12.5px] text-fg-subtle">
            Connect your Robinhood Agentic account. You sign in on robinhood.com — your password is never entered here.
          </p>
        </div>
        {st?.connected && (
          <span className="mt-0.5 shrink-0 rounded-full border border-[color-mix(in_oklab,var(--color-up)_45%,transparent)] px-2 py-0.5 text-[10.5px] font-medium text-up">
            Connected
          </span>
        )}
      </div>

      {(st?.connected || needsLogin) && (
        <div className="mt-3 divide-y divide-[var(--separator)] rounded-lg border border-[var(--line-soft)]">
          <Row label="Status">
            <span className={cn(st!.connected ? 'text-fg' : 'text-down')}>{st!.connected ? 'Active' : 'Needs to be reconnected'}</span>
            {st!.lastError && <span className="mt-0.5 block text-[11px] text-fg-faint">{st!.lastError}</span>}
          </Row>
          {st!.connected && (
            <Row label="Trading session">
              <span className={cn(st!.session === 'connected' && !st!.tradingBlockedReason ? 'text-fg' : 'text-down')}>
                {st!.session === 'connected' ? (st!.tradingBlockedReason ?? `Ready · Agentic account ${st!.account ?? ''} · real money`) : st!.session === 'connecting' ? 'Connecting…' : 'Not running'}
              </span>
            </Row>
          )}
          {st!.connectedAt && <Row label="Connected">{when(st!.connectedAt)}</Row>}
          {st!.toolCount != null && <Row label="Robinhood functions available">{st!.toolCount}</Row>}
        </div>
      )}

      {tools && (
        <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-[var(--line-soft)] px-3 py-2">
          {tools.map((t) => (
            <div key={t.name} className="py-1 text-[11.5px]">
              <span className="num text-fg">{t.name}</span>
              {t.description && <span className="text-fg-faint"> — {t.description}</span>}
            </div>
          ))}
        </div>
      )}

      {signIn && !st?.connected && (
        <div className="mt-3 rounded-lg border border-[var(--line-soft)] p-3 text-[12.5px] text-fg-subtle">
          <ol className="list-decimal space-y-1 pl-4">
            <li>
              Sign in and approve on Robinhood (
              <a href={signIn} target="_blank" rel="noopener noreferrer" className="text-fg underline">
                open the page again
              </a>
              ).
            </li>
            <li>
              The browser then lands on a page that cannot be opened (its address starts with <span className="num text-fg">http://127.0.0.1:8765</span>). That is expected.
            </li>
            <li>Copy that page’s whole address and paste it here.</li>
          </ol>
          <div className="mt-2.5 flex items-center gap-2">
            <input
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="http://127.0.0.1:8765/oauth/callback?code=…"
              spellCheck={false}
              autoComplete="off"
              className="num min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[12px] text-fg outline-none"
            />
            <button
              onClick={() => void finish()}
              disabled={busy || !pasted.trim()}
              className="shrink-0 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--color-base)] disabled:opacity-50"
            >
              Finish
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!st?.connected ? (
          <button
            onClick={() => void connect()}
            disabled={busy || !st}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--color-base)] disabled:opacity-50"
          >
            {needsLogin ? 'Reconnect Robinhood' : 'Connect Robinhood'} <ExternalLink size={12} />
          </button>
        ) : confirmOff ? (
          <>
            <button onClick={() => void disconnect()} disabled={busy} className="rounded-lg border border-[var(--line)] px-3 py-1.5 text-[12.5px] font-medium text-down disabled:opacity-50">
              Disconnect and delete the tokens
            </button>
            <button onClick={() => setConfirmOff(false)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
              Cancel
            </button>
          </>
        ) : (
          <>
            <button onClick={() => void check()} disabled={busy} className="rounded-lg border border-[var(--line)] px-3 py-1.5 text-[12.5px] font-medium text-fg disabled:opacity-50">
              {busy ? 'Checking…' : 'Check the connection'}
            </button>
            <button onClick={() => setConfirmOff(true)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
              Disconnect
            </button>
          </>
        )}
      </div>

      {msg && <div className={cn('mt-2.5 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
      <div className="mt-2.5 text-[11px] text-fg-faint">
        Trades can only reach the Agentic account you open at Robinhood for this; your other Robinhood accounts stay read-only. Workers and chat
        orders can run here like at IBKR: each lot’s stop rests at Robinhood, targets and rules are run by this server on live quotes.
      </div>
    </section>
  )
}
